// src/components/assessment/ResultsView.jsx
import React, { useState, useEffect } from 'react';
import {
    Container, Paper, Typography, Box, Grid, Divider,
    Chip, CircularProgress, Button, Alert
} from '@mui/material';
import {
    CloudDownload as DownloadIcon,
    Share as ShareIcon,
    Print as PrintIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { getAssessmentById } from '../../services/assessmentService';
import RiskGauge from '../common/RiskGauge';
import { formatDate } from '../../utils/formatters';

// Add print-specific styles
const printStyles = `
@media print {
  @page {
    size: auto;
    margin: 10mm;
  }
  
  body * {
    visibility: hidden;
  }
  
  #printable-content, #printable-content * {
    visibility: visible;
  }
  
  #printable-content {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    padding: 20px;
  }
  
  .no-print {
    display: none !important;
  }
  
  .page-break {
    page-break-before: always;
  }
}
`;

const ResultsView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [assessment, setAssessment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAssessment = async () => {
            try {
                setLoading(true);
                const data = await getAssessmentById(id);
                setAssessment(data);
            } catch (err) {
                setError('Failed to load assessment details. Please try again later.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchAssessment();

        // Add print styles to document
        const styleElement = document.createElement('style');
        styleElement.innerHTML = printStyles;
        document.head.appendChild(styleElement);

        // Cleanup
        return () => {
            document.head.removeChild(styleElement);
        };
    }, [id]);

    const handleNewAssessment = () => {
        navigate('/new-assessment');
    };

    const handlePrint = () => {
        window.print();
    };

    const handleBackToDashboard = () => {
        navigate('/dashboard');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Alert severity="error">{error}</Alert>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleBackToDashboard}
                    >
                        Back to Dashboard
                    </Button>
                </Box>
            </Container>
        );
    }

    if (!assessment) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Alert severity="info">Assessment not found.</Alert>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleBackToDashboard}
                    >
                        Back to Dashboard
                    </Button>
                </Box>
            </Container>
        );
    }

    // Determine risk level based on score
    const getRiskLevel = (score) => {
        if (score >= 7) return { level: 'High', color: 'error.main' };
        if (score >= 4) return { level: 'Medium', color: 'warning.main' };
        return { level: 'Low', color: 'success.main' };
    };

    const riskInfo = getRiskLevel(assessment.risk_score);

    // Format domain name
    const formatDomainName = (domain) => {
        return domain.charAt(0).toUpperCase() + domain.slice(1);
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Navigation buttons (only visible on screen) */}
            <Box className="no-print" sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBackToDashboard}
                >
                    Back to Dashboard
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                >
                    Print Report
                </Button>
            </Box>

            {/* Printable Content */}
            <Paper elevation={3} sx={{ p: 4 }} id="printable-content">
                {/* Report Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            {assessment.title}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {formatDate(assessment.created_at)} | Domain:&nbsp;
                            <Chip
                                label={formatDomainName(assessment.domain)}
                                size="small"
                                sx={{ ml: 1 }}
                            />
                        </Typography>
                    </Box>

                    {/* Company Logo or Assessment ID */}
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Assessment ID: {assessment.id}
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ mb: 4 }} />

                <Grid container spacing={4}>
                    <Grid item xs={12} md={4}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}
                        >
                            <Typography variant="h6" gutterBottom>
                                Overall Risk Score
                            </Typography>
                            <RiskGauge value={assessment.risk_score} />
                            <Typography
                                variant="h5"
                                sx={{
                                    mt: 2,
                                    color: riskInfo.color
                                }}
                            >
                                {riskInfo.level} Risk
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Typography variant="h6" gutterBottom>
                            Risk Analysis
                        </Typography>

                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                Key Risk Factors
                            </Typography>

                            <Box sx={{ pl: 2 }}>
                                {assessment.risk_factors ?
                                    Object.entries(assessment.risk_factors).map(([key, value]) => (
                                        <Box key={key} sx={{ mb: 1 }}>
                                            <Typography variant="body1">
                                                <strong>{key.replace(/_/g, ' ').toUpperCase()}:</strong> {value.toString()}
                                            </Typography>
                                        </Box>
                                    )) :
                                    <Typography>No risk factors identified.</Typography>
                                }
                            </Box>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                AI Analysis
                            </Typography>

                            {assessment.ai_analysis ? (
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                    {assessment.ai_analysis.analysis || assessment.ai_analysis.raw_analysis}
                                </Typography>
                            ) : (
                                <Typography>No AI analysis available.</Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                {/* Recommendations Section */}
                {assessment.ai_analysis && assessment.ai_analysis.structured_data && assessment.ai_analysis.structured_data.recommendations && (
                    <Box sx={{ mt: 4 }}>
                        <Divider sx={{ mb: 3 }} />
                        <Typography variant="h6" gutterBottom>
                            Recommendations
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            {assessment.ai_analysis.structured_data.recommendations.length > 0 ? (
                                <Box component="ul" sx={{ pl: 2, ml: 1 }}>
                                    {assessment.ai_analysis.structured_data.recommendations.map((rec, index) => (
                                        <Box component="li" key={index} sx={{ mb: 1 }}>
                                            <Typography variant="body1">{rec}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography>No specific recommendations available.</Typography>
                            )}
                        </Paper>
                    </Box>
                )}

                {/* Report Footer - only visible in print */}
                <Box
                    sx={{
                        mt: 6,
                        pt: 2,
                        borderTop: '1px solid #eee',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Generated by RiskAS on {new Date().toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Page 1 of 1
                    </Typography>
                </Box>

                {/* Action Buttons - not for print */}
                <Box className="no-print" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        onClick={handleNewAssessment}
                    >
                        Create New Assessment
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default ResultsView;